import { ApiResponse } from "@/types/api.type";
import api from "../api";
import {
  BalanceChartDataMain,
  CashWalletBalanceDataMain,
  DetailTransferData,
  ListClubsDaum,
  ListMerchantData,
  ListUserClubData,
  NoduedateData,
  PazirandeClubsDaum,
  UserInfoData,
} from "../../types/api/merchant/merchant.type";
import {GetGoldLogsParams, GoldLogsApiResponse , GoldLogDetailsResponse} from "@/types/api/merchant/gold-logs.types";

/**
 * fetchListClubs
 *
 * دریافت لیست تمام کلاب‌ها.
 *
 * @returns Promise<Array<ListClubsDaum>>
 */
export const fetchListClubs = async (): Promise<ListClubsDaum[]> => {
  const res = await api.get<ApiResponse<ListClubsDaum[]>>(
    `/20card/api/pazirande/list-clubs`
  );
  return res.data?.data;
};

/**
 * fetchDetailListClubUsers
 *
 * دریافت لیست کاربران یک کلاب خاص.
 *
 * @param id شناسه کلاب
 * @returns Promise<Array<ListUserClubData>>
 */
export const fetchDetailListClubUsers = async (
  id: string | undefined,
  search?: string,
  page: number = 1,
  per_page: number = 10 
): Promise<ListUserClubData[]> => {
  let url = `/20card/api/pazirande/list-club-users/${id}?page=${page}&per_page=${per_page}`;
  
  if (search && search.trim() !== "") {
    url += `&search=${encodeURIComponent(search.trim())}`;
  }
  
  const res = await api.get<ApiResponse<ListUserClubData[]>>(url);
  return res.data?.data;
};

/**
 * fetchDetailClubUsers
 *
 * دریافت اطلاعات کامل یک کاربر از کلاب.
 *
 * @param id شناسه کاربر
 * @returns Promise<UserInfoData>
 */
export const fetchDetailClubUsers = async (
  id: string | undefined
): Promise<UserInfoData> => {
  const res = await api.get<ApiResponse<UserInfoData>>(
    `/20card/api/pazirande/details-club-users/${id}`
  );
  return res.data?.data;
};

/**
 * fetchDetailTransfer
 *
 * دریافت جزئیات تراکنش‌های یک کاربر با امکان صفحه‌بندی و فیلتر نوع تراکنش.
 *
 * @param id شناسه کاربر
 * @param type نوع تراکنش (cash/credit)
 * @param page شماره صفحه
 * @param selectedNumber تعداد نتایج هر صفحه
 * @returns Promise<DetailTransferData>
 */
export const fetchDetailTransfer = async (
  id: string | undefined,
  type: string,
  page: number,
  selectedNumber: string
): Promise<DetailTransferData> => {
  const res = await api.get<ApiResponse<DetailTransferData>>(
    `/20card/api/pazirande/list-user-club-transactions/${id}?type=${type}&page=${page}&size=${selectedNumber}`
  );
  return res.data?.data;
};

/**
 * fetchListListMerchantName
 *
 * دریافت اطلاعات تجاری مربوط به پذیرنده.
 *
 * @returns Promise<ListMerchantData>
 */
export const fetchListListMerchantName =
  async (): Promise<ListMerchantData> => {
    const res = await api.get<ApiResponse<ListMerchantData>>(
      `/20card/api/pazirande/list-merchant-name`
    );
    return res.data?.data;
  };

/**
 * fetchListChargeWalletPazirande
 *
 * دریافت لیست شارژ کیف پول پذیرنده
 *
 * @returns Promise<any>
 */
export const fetchListChargeWalletPazirande = async (): Promise<any> => {
  const res = await api.get<ApiResponse<any>>(
    `/20card/api/pazirande/list-charge-wallet-pazirande`
  );
  return res.data;
};

/**
 * fetchListTransaction
 *
 * دریافت لیست تراکنش‌های کیف پول با فیلتر ref_type=transfer
 *
 * @param page شماره صفحه
 * @param count تعداد نتایج در هر صفحه
 * @returns Promise<any>
 */
export const fetchListTransaction = async (
  page: string | number,
  count: string | number,
): Promise<any> => {
  const res = await api.get<ApiResponse<any>>(
    `/wallet/api/v1/transaction?page=${page}&count=${count}&ref_type=transfer`
  );
  return res.data?.data;
};

/**
 * fetchCashWalletBalance
 *
 * دریافت موجودی نقدی کیف پول پذیرنده
 *
 * @returns Promise<CashWalletBalanceDataMain>
 */
export const fetchCashWalletBalance =
  async (): Promise<CashWalletBalanceDataMain> => {
    const res = await api.get<ApiResponse<CashWalletBalanceDataMain>>(
      `/20card/api/pazirande/cash-wallet-balance?page=1&search=10`
    );
    return res.data?.data;
  };

/**
 * fetchNoduedateCreditBalance
 *
 * دریافت موجودی اعتبار بدون موعد با صفحه‌بندی
 *
 * @param page شماره صفحه
 * @returns Promise<NoduedateData>
 */
export const fetchNoduedateCreditBalance = async (
  page: string | number,
  per_page:number
): Promise<NoduedateData> => {
  const res = await api.get<ApiResponse<NoduedateData>>(
    `/20card/api/pazirande/noduedate-credit-balance?page=${page}&size=${per_page}`
  );
  return res.data?.data;
};

/**
 * fetchNoduedateCreditBalanceChart
 *
 * دریافت داده‌های نمودار موجودی اعتبار بدون موعد
 *
 * @returns Promise<BalanceChartDataMain>
 */
export const fetchNoduedateCreditBalanceChart =
  async (): Promise<BalanceChartDataMain> => {
    const res = await api.get<ApiResponse<BalanceChartDataMain>>(
      `/20card/api/pazirande/list-noduedate-credit-balance-chart??ids[]=34&ids[]=10`
    );
    return res.data?.data;
  };

/**
 * fetchListPazirandeClubs
 *
 * دریافت لیست کلاب‌های پذیرنده
 *
 * @returns Promise<Array<PazirandeClubsDaum>>
 */
export const fetchListPazirandeClubs = async (): Promise<
  PazirandeClubsDaum[]
> => {
  const res = await api.get<ApiResponse<PazirandeClubsDaum[]>>(
    `/20card/api/pazirande/list-pazirande-clubs`
  );
  return res.data?.data;
};

/**
 * fetchListPazirandeBankAccounts
 *
 * دریافت لیست حساب‌های بانکی پذیرنده
 *
 * @returns Promise<any[]>
 */
export const fetchListPazirandeBankAccounts = async (): Promise<any[]> => {
  const res = await api.get<ApiResponse<any[]>>(
    `/20card/api/pazirande/list-pazirande-bank-accounts`
  );
  return res.data?.data;
};

/**
 * fetchListGetBalancePazirande
 *
 * دریافت موجودی کل پذیرنده
 *
 * @returns Promise<any>
 */
export const fetchListGetBalancePazirande = async (): Promise<any> => {
  const res = await api.get<ApiResponse<any>>(
    `/20card/api/pazirande/get-balance-pazirande`
  );
  return res.data?.data;
};

/**
 * fetchListLastWithdrawRequestPazirande
 *
 * دریافت آخرین درخواست برداشت پذیرنده
 *
 * @returns Promise<any>
 */
export const fetchListLastWithdrawRequestPazirande = async (): Promise<any> => {
  const res = await api.get<ApiResponse<any>>(
    `/20card/api/pazirande/last-withdraw-request-pazirande`
  );
  return res.data?.data;
};

/**
 * fetchListIncreaseBalance
 *
 * دریافت تراکنش‌های افزایش موجودی هدیه کاربران
 *
 * @returns Promise<any[]>
 */
export const fetchListIncreaseBalance = async (): Promise<any[]> => {
  const res = await api.get<ApiResponse<any[]>>(
    `/20card/api/pazirande/list-deposit-gift-user-transactions/206`
  );
  return res.data?.data;
};

/**
 * fetchListCashTransactionList
 *
 * دریافت لیست تراکنش‌های نقدی بر اساس نوع تراکنش و صفحه
 *
 * @param type نوع تراکنش
 * @param page شماره صفحه
 * @returns Promise<any>
 */
export const fetchListCashTransactionList = async (
  type: string,
  page: number
): Promise<any> => {
  const res = await api.get<ApiResponse<any>>(
    `/20card/api/merchant/cash-transaction-list?page=${page}&transaction_type=${type}`
  );
  return res.data;
};

/**
 * fetchListChartCashTransactionList
 *
 * دریافت داده‌های نموداری تراکنش‌های نقدی
 *
 * @param transaction_type نوع تراکنش
 * @param type نوع فیلتر
 * @param page شماره صفحه
 * @returns Promise<any>
 */
export const fetchListChartCashTransactionList = async (
  transaction_type: string,
  type: string,
  page: number
): Promise<any> => {
  const res = await api.get<ApiResponse<any>>(
    `/20card/api/merchant/chart-cash-transaction-list?page=${page}&transaction_type=${transaction_type}&type=${type}`
  );
  return res.data;
};

/**
 * fetchListAllTransactionList
 *
 * دریافت تمامی تراکنش‌ها با قابلیت جستجو و صفحه‌بندی
 *
 * @param page شماره صفحه
 * @param searhcText متن جستجو
 * @returns Promise<any>
 */
export const fetchListAllTransactionList = async (
  page: number,
  searhcText?: string,
  per_page?:number
): Promise<any> => {
  const res = await api.get<ApiResponse<any>>(
    `/20card/api/merchant/all-transaction-list?page=${page}&search=${searhcText}&per_page=${per_page}`
  );
  return res.data;
};

/**
 * fetchListWithdrawableCreditBalance
 *
 * دریافت موجودی اعتبار قابل برداشت پذیرنده
 *
 * @returns Promise<any>
 */
export const fetchListWithdrawableCreditBalance = async (
  page: number,
  per_page?:number
): Promise<any> => {
  const res = await api.get<ApiResponse<any>>(
    `/20card/api/pazirande/withdrawable-credit-balance?page=${page}&per_page=${per_page}`
  );
  return res.data;
};

/**
 * fetchWithdrawableCreditBalanceChart
 *
 * دریافت داده‌های نموداری موجودی اعتبار قابل برداشت
 *
 * @returns Promise<any>
 */
export const fetchWithdrawableCreditBalanceChart = async (): Promise<any> => {
  const res = await api.get<ApiResponse<any>>(
    `/20card/api/pazirande/withdrawable-credit-balance-chart?ids[]=34`
  );
  return res.data?.data;
};

/**
 * fetchListActivePazirandeBankAccounts
 *
 * دریافت لیست حساب‌های بانکی فعال پذیرنده
 *
 * @returns Promise<any>
 */
export const fetchListActivePazirandeBankAccounts = async (): Promise<any> => {
  const res = await api.get<ApiResponse<any>>(
    `/20card/api/pazirande/list-active-pazirande-bank-accounts`
  );
  return res.data;
};

/**
 * fetchPazirandeProfileInfo
 *
 * دریافت اطلاعات پذیرنده
 *
 * @returns Promise<any>
 */
export const fetchPazirandeProfileInfo = async (): Promise<any> => {
  const res = await api.get<ApiResponse<any>>(
    `/20card/api/pazirande/profile-info`
  );
  return res.data;
};


export const fetchPazirandeProfile = async (): Promise<any> => {
  const res = await api.get<ApiResponse<any>>(
    `/20card/api/pazirande/profile-info`
  );
  return res.data.data;
};

export const fetchListMerchantMessage = async (page = 1, per_page = 10) => {
  const res = await api.get(`20card/api/merchant/list-merchant-message?page=${page}&per_page=${per_page}`);
  return res.data;
};

export const fetchShowMerchantMessage = async (id:number) => {
  const res = await api.get(`20card/api/merchant/show-merchant-message/${id}`);
  return res.data;
};

export const fetchCountMerchantMessage = async () => {
  const res = await api.get(`20card/api/merchant/count-merchant-message`);
  return res.data;
};

export const fetchFailedTransactions = async ({ cardNumber, clubId }: { cardNumber?: string; clubId?: string }) => {
  // Build query parameters dynamically, only including defined values
  const params = new URLSearchParams();
  
  if (cardNumber) {
    params.append('cardNumber', cardNumber);
  }
  if (clubId) {
    params.append('clubId', clubId);
  }
  
  const queryString = params.toString();
  const url = `20card/api/pazirande/failed-transactions/${queryString ? `?${queryString}` : ''}`;
  
  const res = await api.get(url);
  return res.data;
};

export const fetchGoldLogsMerchant = async (params?: GetGoldLogsParams): Promise<GoldLogsApiResponse> => {
  const res = await api.get<GoldLogsApiResponse>(`extra/api/gold-logs-merchant`, {
    params: {
      per_page: params?.per_page || 10,
      page: params?.page || 1,
    }
  });
  return res.data;
};

export const fetchGoldLogDetails = async (id: number): Promise<GoldLogDetailsResponse> => {
  const res = await api.post<GoldLogDetailsResponse>(`extra/api/gold-logs-merchant-details`, {
    id: id
  });
  return res.data;
};